import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled, {keyframes} from 'styled-components'

export interface Props {
    itemWidth: number
    speed?: number
    children: React.ReactNode
}

export const Marquee = ({itemWidth, speed, children, ...props}: Props) => {
    const [ref, isInView] = useInView()

    return (
        <Wrapper {...props}>
            <Track
                ref={ref}
                $isAnimating={isInView}
                $childrenCount={React.Children.count(children)}
                $itemWidth={itemWidth}
                $speed={speed || 5}
            >
                {/* We need two chunks of children so they can slide into view as the first chunk slides out */}
                {Array(4)
                    .fill(null)
                    .map((_, i) =>
                        React.Children.map(children, (child, index) => (
                            <Item key={`${index}-${i}`}>{child}</Item>
                        )),
                    )}
            </Track>
        </Wrapper>
    )
}

const marqueeAnimation = keyframes`
100% {
    transform: translateX(-50%);
}
`

const Wrapper = styled.div`
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;

    &:hover > ul {
        animation-play-state: paused;
    }
`

interface TrackProps {
    $isAnimating: boolean
    $childrenCount: number
    $speed: number
    $itemWidth: number
}

const Track = styled.ul<TrackProps>`
    display: flex;
    position: absolute;
    overflow: hidden;
    height: 100%;
    align-items: center;
    animation: ${marqueeAnimation}
        ${({$childrenCount, $itemWidth, $speed}) => ($childrenCount * $itemWidth) / $speed}s linear
        infinite;
    animation-play-state: ${({$isAnimating}) => ($isAnimating ? 'running' : 'paused')};
    margin: 0;
    padding: 0;
`

const Item = styled.li`
    flex: 1;
    list-style: none;
    margin-left: 40px;
    margin-right: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
`
